/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import type * as Types from '../../../../__generated__/zeus/types';

import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';
/** Selects individual markers or clusters based on the viewport and listing density. */
export type MarketplaceAdaptiveMapSearchInput = {
  /** Closed polygon representing the visible map viewport. */
  viewport: Array<MarketplaceGeoPointInput>;
};

/** Customer-facing badge describing a marketplace home’s availability. */
export type MarketplaceDisplayBadge =
  /** The home is expected to become available soon. */
  | 'AVAILABLE_SOON'
  /** The home is under contract. */
  | 'IN_CONTRACT'
  /** The home is on the market and eligible for self-tour. */
  | 'ON_THE_MARKET'
  /** The home recently sold. */
  | 'RECENTLY_SOLD'
  /** The home is on the market but cannot currently be accessed for self-tour. */
  | 'TEMPORARILY_UNAVAILABLE'
  /** The home is on the market but does not support self-tour. */
  | 'TRADITIONAL'
  /** The home’s display status is unavailable. */
  | 'UNKNOWN';

/** Home type used to classify a marketplace listing. */
export type MarketplaceDwellingType =
  /** A condominium home. */
  | 'CONDO'
  /** A detached or single-family house. */
  | 'HOUSE'
  /** A property containing multiple residential units. */
  | 'MULTI_FAMILY'
  /** A dwelling type that does not fit the listed categories. */
  | 'OTHER'
  /** A townhouse. */
  | 'TOWNHOUSE';

/** Geographic coordinate in decimal degrees. */
export type MarketplaceGeoPointInput = {
  /** Latitude in decimal degrees, from -90 to 90. */
  lat: Types.Scalars['Latitude']['input'];
  /** Longitude in decimal degrees, from -180 to 180. */
  lon: Types.Scalars['Longitude']['input'];
};

/** Filters applied to marketplace listing results. */
export type MarketplaceListingFiltersInput = {
  /** When true, return only homes expected to become available for purchase soon; omit or set false to return homes currently on the market. */
  availableSoon?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** When true, return homes currently eligible for self-tour. */
  canSelfTour?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** Return only homes whose dwelling type is one of these values. */
  dwellingTypes?: Array<MarketplaceDwellingType> | null | undefined;
  /** When true, return homes with a pool. */
  hasPool?: Types.Scalars['Boolean']['input'] | null | undefined;
  /** Maximum monthly homeowners association dues, in US cents. */
  maxHoaMonthlyFeeCents?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Maximum list price, in US cents. */
  maxListPriceCents?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Maximum lot area in square feet. */
  maxLotSquareFootage?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Maximum interior living area in square feet. */
  maxSquareFootage?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Maximum full home area in square feet, including basement area when available. */
  maxTotalSquareFootage?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Latest allowed construction year. */
  maxYearBuilt?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Minimum number of bathrooms; half bathrooms may be represented as .5. */
  minBathrooms?: Types.Scalars['Float']['input'] | null | undefined;
  /** Minimum number of bedrooms. */
  minBedrooms?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Minimum list price, in US cents. */
  minListPriceCents?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Minimum lot area in square feet. */
  minLotSquareFootage?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Minimum interior living area in square feet. */
  minSquareFootage?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Minimum full home area in square feet, including basement area when available. */
  minTotalSquareFootage?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Earliest allowed construction year. */
  minYearBuilt?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** When true, return only homes with a single-story layout. */
  singleStoryOnly?: Types.Scalars['Boolean']['input'] | null | undefined;
};

/** Available sort keys for marketplace listing results. */
export type MarketplaceListingSort =
  /** Sort by listing date from oldest to newest. */
  | 'LIST_DATE_ASC'
  /** Sort by listing date from newest to oldest. */
  | 'LIST_DATE_DESC'
  /** Sort by list price from lowest to highest. */
  | 'LIST_PRICE_ASC'
  /** Sort by list price from highest to lowest. */
  | 'LIST_PRICE_DESC'
  /** Sort by interior living area from smallest to largest. */
  | 'SQUARE_FOOTAGE_ASC'
  /** Sort by interior living area from largest to smallest. */
  | 'SQUARE_FOOTAGE_DESC'
  /** Sort by construction year from oldest to newest. */
  | 'YEAR_BUILT_ASC'
  /** Sort by construction year from newest to oldest. */
  | 'YEAR_BUILT_DESC';

/** Current customer-facing status of a marketplace listing. */
export type MarketplaceListingState =
  /** The home is expected to become available for purchase soon. */
  | 'AVAILABLE_SOON'
  /** The home is under contract with a buyer. */
  | 'IN_CONTRACT'
  /** The home is currently offered for purchase. */
  | 'ON_THE_MARKET'
  /** The home has recently sold. */
  | 'RECENTLY_SOLD'
  /** The home’s current marketplace status could not be determined. */
  | 'UNKNOWN';

/** Filters, geographic scope, sorting, and pagination for a listing search. */
export type MarketplaceListingsInput = {
  /** Cursor returned in pageInfo.endCursor. Cannot be combined with page. */
  after?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
  /** Optional home attributes used to narrow matching listings. */
  filters?: MarketplaceListingFiltersInput | null | undefined;
  /** Maximum listings to return. Defaults to 30 and cannot exceed 100. */
  first?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Zero-based page to return. The requested page must fall within the first 10,000 matches and cannot be combined with after. */
  page?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Photos to return per listing, from 1 to 3. Defaults to 3. */
  photoLimit?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Sort fields in priority order. Defaults to newest listings first. */
  sortBy?: Array<MarketplaceListingSort> | null | undefined;
  /** Area containing listings to return; omit to search all supported marketplace listings. */
  where?: MarketplaceSearchAreaInput | null | undefined;
};

/** Filters and result mode for a map search. */
export type MarketplaceMapSearchInput = {
  /** Optional home attributes used to narrow map matches. */
  filters?: MarketplaceListingFiltersInput | null | undefined;
  /** Whether map results use adaptive viewport behavior or individual markers. */
  mode: MarketplaceMapSearchModeInput;
};

/** Result mode for a map search. Provide exactly one option. */
export type MarketplaceMapSearchModeInput =
  {   /** Let the server choose markers or clusters based on viewport density. */
  adaptive: MarketplaceAdaptiveMapSearchInput; markers?: never; }
  |  { adaptive?: never;   /** Return up to 10,000 individual markers for the selected area. */
  markers: MarketplaceMarkerMapSearchInput; };

/** Returns up to 10,000 individual listing markers for an area. */
export type MarketplaceMarkerMapSearchInput = {
  /** Geographic area for which to return individual listing markers. */
  area: MarketplaceSearchAreaInput;
};

/** Geographic scope for listing search. Provide exactly one option. */
export type MarketplaceSearchAreaInput =
  {   /** Market identifier to search, up to 64 characters. */
  market: Types.Scalars['NonEmptyString']['input']; vertices?: never; zoneIds?: never; }
  |  { market?: never;   /** Closed polygon defining the search area in decimal degrees; use 4 to 256 vertices. */
  vertices: Array<MarketplaceGeoPointInput>; zoneIds?: never; }
  |  { market?: never; vertices?: never;   /** Up to 128 IDs of zones whose listings should be included. */
  zoneIds: Array<Types.Scalars['UUID']['input']>; };

export type MarketplaceBrowseListingCardFragment = { addressId: Types.Scalars['UUID']['output'], bathrooms: Types.Scalars['Float']['output'] | null, bedrooms: Types.Scalars['SafeInt']['output'] | null, canSelfTour: Types.Scalars['Boolean']['output'] | null, city: Types.Scalars['String']['output'] | null, displayBadge: Types.MarketplaceDisplayBadge, dwellingType: Types.MarketplaceDwellingType, fullAddress: Types.Scalars['String']['output'] | null, listingState: Types.MarketplaceListingState, listPriceCents: Types.Scalars['SafeInt']['output'] | null, neighborhood: Types.Scalars['String']['output'] | null, photoUrls: Array<Types.Scalars['URL']['output']>, slug: Types.Scalars['String']['output'] | null, state: Types.Scalars['String']['output'] | null, street: Types.Scalars['String']['output'] | null, totalLivingSquareFootage: Types.Scalars['SafeInt']['output'] | null, location: { latitude: Types.Scalars['Latitude']['output'], longitude: Types.Scalars['Longitude']['output'] } | null };

export type MarketplaceBrowseMapMarkerFragment = { addressId: Types.Scalars['UUID']['output'], displayBadge: Types.MarketplaceDisplayBadge, bathrooms: Types.Scalars['Float']['output'] | null, bedrooms: Types.Scalars['SafeInt']['output'] | null, listPriceCents: Types.Scalars['SafeInt']['output'] | null, listingState: Types.MarketplaceListingState, photoUrls: Array<Types.Scalars['URL']['output']>, street: Types.Scalars['String']['output'] | null, totalLivingSquareFootage: Types.Scalars['SafeInt']['output'] | null, location: { latitude: Types.Scalars['Latitude']['output'], longitude: Types.Scalars['Longitude']['output'] } };

export type MarketplaceBrowseActiveMarketFragment = { displayName: Types.Scalars['NonEmptyString']['output'], displayState: Types.Scalars['NonEmptyString']['output'], homesPath: Types.Scalars['NonEmptyString']['output'], identifier: Types.Scalars['NonEmptyString']['output'] };

export type MarketplaceActiveMarketsQueryVariables = Exact<{
  first?: Types.Scalars['SafeInt']['input'] | null | undefined;
}>;


export type MarketplaceActiveMarketsQuery = { marketplace: { activeBuyMarkets: Array<{ displayName: Types.Scalars['NonEmptyString']['output'], displayState: Types.Scalars['NonEmptyString']['output'], homesPath: Types.Scalars['NonEmptyString']['output'], identifier: Types.Scalars['NonEmptyString']['output'] }> } };

export type MarketplaceBrowseQueryVariables = Exact<{
  input: Types.MarketplaceListingsInput;
}>;


export type MarketplaceBrowseQuery = { marketplace: { listings: { nextPage: Types.Scalars['SafeInt']['output'] | null, totalCount: Types.Scalars['SafeInt']['output'], listings: Array<{ addressId: Types.Scalars['UUID']['output'], bathrooms: Types.Scalars['Float']['output'] | null, bedrooms: Types.Scalars['SafeInt']['output'] | null, canSelfTour: Types.Scalars['Boolean']['output'] | null, city: Types.Scalars['String']['output'] | null, displayBadge: Types.MarketplaceDisplayBadge, dwellingType: Types.MarketplaceDwellingType, fullAddress: Types.Scalars['String']['output'] | null, listingState: Types.MarketplaceListingState, listPriceCents: Types.Scalars['SafeInt']['output'] | null, neighborhood: Types.Scalars['String']['output'] | null, photoUrls: Array<Types.Scalars['URL']['output']>, slug: Types.Scalars['String']['output'] | null, state: Types.Scalars['String']['output'] | null, street: Types.Scalars['String']['output'] | null, totalLivingSquareFootage: Types.Scalars['SafeInt']['output'] | null, location: { latitude: Types.Scalars['Latitude']['output'], longitude: Types.Scalars['Longitude']['output'] } | null }> } } };

export type MarketplaceBrowseMapQueryVariables = Exact<{
  input: Types.MarketplaceMapSearchInput;
}>;


export type MarketplaceBrowseMapQuery = { marketplace: { mapSearch:
      | { __typename: 'MarketplaceMapSearchClustersResult' }
      | { __typename: 'MarketplaceMapSearchMarkersResult', markers: Array<{ addressId: Types.Scalars['UUID']['output'], displayBadge: Types.MarketplaceDisplayBadge, bathrooms: Types.Scalars['Float']['output'] | null, bedrooms: Types.Scalars['SafeInt']['output'] | null, listPriceCents: Types.Scalars['SafeInt']['output'] | null, listingState: Types.MarketplaceListingState, photoUrls: Array<Types.Scalars['URL']['output']>, street: Types.Scalars['String']['output'] | null, totalLivingSquareFootage: Types.Scalars['SafeInt']['output'] | null, location: { latitude: Types.Scalars['Latitude']['output'], longitude: Types.Scalars['Longitude']['output'] } }> }
     } };

export type MarketplaceMarketRouteDataQueryVariables = Exact<{
  marketIdentifier: Types.Scalars['NonEmptyString']['input'];
}>;


export type MarketplaceMarketRouteDataQuery = { marketplace: { market: { active: Types.Scalars['Boolean']['output'], displayName: Types.Scalars['NonEmptyString']['output'], identifier: Types.Scalars['NonEmptyString']['output'], shape: { rings: Array<{ points: Array<{ latitude: Types.Scalars['Latitude']['output'], longitude: Types.Scalars['Longitude']['output'] }> }> } | null } | null } };

export type MarketplaceZoneRouteDataQueryVariables = Exact<{
  zoneId: Types.Scalars['UUID']['input'];
}>;


export type MarketplaceZoneRouteDataQuery = { marketplace: { zone: { boundingBox: Types.Scalars['String']['output'] | null, name: Types.Scalars['NonEmptyString']['output'] } | null } };

export type MarketplaceAvailableSoonCountQueryVariables = Exact<{
  where: Types.MarketplaceSearchAreaInput;
}>;


export type MarketplaceAvailableSoonCountQuery = { marketplace: { availableSoonCount: Types.Scalars['SafeInt']['output'] | null } };

export class TypedDocumentString<TResult, TVariables>
  extends String
  implements DocumentTypeDecoration<TResult, TVariables>
{
  __apiType?: NonNullable<DocumentTypeDecoration<TResult, TVariables>['__apiType']>;
  private value: string;
  public __meta__?: Record<string, any> | undefined;

  constructor(value: string, __meta__?: Record<string, any> | undefined) {
    super(value);
    this.value = value;
    this.__meta__ = __meta__;
  }

  override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
    return this.value;
  }
}
export const MarketplaceBrowseListingCardFragmentDoc = /*#__PURE__*/ new TypedDocumentString(`
    fragment MarketplaceBrowseListingCard on MarketplaceListing {
  addressId
  bathrooms
  bedrooms
  canSelfTour
  city
  displayBadge
  dwellingType
  fullAddress
  listingState
  listPriceCents
  location {
    latitude
    longitude
  }
  neighborhood
  photoUrls(first: 3)
  slug
  state
  street
  totalLivingSquareFootage
}
    `, {"fragmentName":"MarketplaceBrowseListingCard"}) as unknown as TypedDocumentString<MarketplaceBrowseListingCardFragment, unknown>;
export const MarketplaceBrowseMapMarkerFragmentDoc = /*#__PURE__*/ new TypedDocumentString(`
    fragment MarketplaceBrowseMapMarker on MarketplaceMapMarker {
  addressId
  displayBadge
  bathrooms
  bedrooms
  listPriceCents
  listingState
  location {
    latitude
    longitude
  }
  photoUrls
  street
  totalLivingSquareFootage
}
    `, {"fragmentName":"MarketplaceBrowseMapMarker"}) as unknown as TypedDocumentString<MarketplaceBrowseMapMarkerFragment, unknown>;
export const MarketplaceBrowseActiveMarketFragmentDoc = /*#__PURE__*/ new TypedDocumentString(`
    fragment MarketplaceBrowseActiveMarket on ActiveBuyMarket {
  displayName
  displayState
  homesPath
  identifier
}
    `, {"fragmentName":"MarketplaceBrowseActiveMarket"}) as unknown as TypedDocumentString<MarketplaceBrowseActiveMarketFragment, unknown>;
export const MarketplaceActiveMarketsDocument = /*#__PURE__*/ new TypedDocumentString(`
    query MarketplaceActiveMarkets($first: SafeInt) {
  marketplace {
    activeBuyMarkets(first: $first) {
      ...MarketplaceBrowseActiveMarket
    }
  }
}
    fragment MarketplaceBrowseActiveMarket on ActiveBuyMarket {
  displayName
  displayState
  homesPath
  identifier
}`) as unknown as TypedDocumentString<MarketplaceActiveMarketsQuery, MarketplaceActiveMarketsQueryVariables>;
export const MarketplaceBrowseDocument = /*#__PURE__*/ new TypedDocumentString(`
    query MarketplaceBrowse($input: MarketplaceListingsInput!) {
  marketplace {
    listings(input: $input) {
      listings {
        ...MarketplaceBrowseListingCard
      }
      nextPage
      totalCount
    }
  }
}
    fragment MarketplaceBrowseListingCard on MarketplaceListing {
  addressId
  bathrooms
  bedrooms
  canSelfTour
  city
  displayBadge
  dwellingType
  fullAddress
  listingState
  listPriceCents
  location {
    latitude
    longitude
  }
  neighborhood
  photoUrls(first: 3)
  slug
  state
  street
  totalLivingSquareFootage
}`) as unknown as TypedDocumentString<MarketplaceBrowseQuery, MarketplaceBrowseQueryVariables>;
export const MarketplaceBrowseMapDocument = /*#__PURE__*/ new TypedDocumentString(`
    query MarketplaceBrowseMap($input: MarketplaceMapSearchInput!) {
  marketplace {
    mapSearch(input: $input) {
      __typename
      ... on MarketplaceMapSearchMarkersResult {
        markers {
          ...MarketplaceBrowseMapMarker
        }
      }
    }
  }
}
    fragment MarketplaceBrowseMapMarker on MarketplaceMapMarker {
  addressId
  displayBadge
  bathrooms
  bedrooms
  listPriceCents
  listingState
  location {
    latitude
    longitude
  }
  photoUrls
  street
  totalLivingSquareFootage
}`) as unknown as TypedDocumentString<MarketplaceBrowseMapQuery, MarketplaceBrowseMapQueryVariables>;
export const MarketplaceMarketRouteDataDocument = /*#__PURE__*/ new TypedDocumentString(`
    query MarketplaceMarketRouteData($marketIdentifier: NonEmptyString!) {
  marketplace {
    market(identifier: $marketIdentifier) {
      active
      displayName
      identifier
      shape {
        rings {
          points {
            latitude
            longitude
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<MarketplaceMarketRouteDataQuery, MarketplaceMarketRouteDataQueryVariables>;
export const MarketplaceZoneRouteDataDocument = /*#__PURE__*/ new TypedDocumentString(`
    query MarketplaceZoneRouteData($zoneId: UUID!) {
  marketplace {
    zone(id: $zoneId) {
      boundingBox
      name
    }
  }
}
    `) as unknown as TypedDocumentString<MarketplaceZoneRouteDataQuery, MarketplaceZoneRouteDataQueryVariables>;
export const MarketplaceAvailableSoonCountDocument = /*#__PURE__*/ new TypedDocumentString(`
    query MarketplaceAvailableSoonCount($where: MarketplaceSearchAreaInput!) {
  marketplace {
    availableSoonCount(where: $where)
  }
}
    `) as unknown as TypedDocumentString<MarketplaceAvailableSoonCountQuery, MarketplaceAvailableSoonCountQueryVariables>;